import { useMemo } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import type { TelemetryNote } from "@/types";
import {
  formatVideoTimelineClock,
  formatVideoTimelineDuration,
  getVideoTimelineTicks,
} from "@/utils/videoTimeline";
import { formatLapTime } from "@/utils/timeFormat";
import type { TimelineRange } from "@/utils/sharedTimeline";
import {
  getVideoLocalTime,
  getVideoSessionClipAtTime,
  type VideoSessionClip,
  type VideoSessionTimeline,
} from "@/utils/videoSessionTimeline";
import { TimelineNoteMarkers } from "@/components/telemetry/TimelineNoteMarkers";
import { TimelineLapMarkers } from "@/components/video/TimelineLapMarkers";
import {
  getLapMarkers,
  type SessionLapMarker,
  type SessionLapTiming,
} from "@/utils/lapTimeline";
import {
  noteTimeOffsetFromSessionTime,
  sessionTimeFromNoteTimeOffset,
} from "@/utils/telemetryNotes";
import { useTranslation } from "react-i18next";

interface VideoSessionTimelineLaneProps {
  timeline: VideoSessionTimeline;
  currentTime: number;
  viewport: TimelineRange | null;
  syncOffset: number;
  notes: TelemetryNote[];
  onTimeChange: (time: number) => void;
  onNoteClick?: (note: TelemetryNote) => void;
  onNoteCreate?: (timeOffset: number) => void;
  notesEnabled?: boolean;
  /** Telemetry-derived laps; rendered as start/finish crossing markers. */
  laps?: SessionLapTiming[];
  onLapClick?: (marker: SessionLapMarker) => void;
}

interface TimelineGap {
  startTime: number;
  endTime: number;
}

const CLIP_COLORS = [
  { background: "#172554", border: "#2563eb" },
  { background: "#312e81", border: "#8b5cf6" },
  { background: "#164e63", border: "#0891b2" },
  { background: "#3f1d38", border: "#db2777" },
] as const;

const getTimelineGaps = (clips: VideoSessionClip[]): TimelineGap[] => {
  const gaps: TimelineGap[] = [];
  let coveredUntil = clips[0]?.endTime ?? 0;

  clips.slice(1).forEach((clip) => {
    if (clip.startTime > coveredUntil) {
      gaps.push({ startTime: coveredUntil, endTime: clip.startTime });
    }
    coveredUntil = Math.max(coveredUntil, clip.endTime);
  });

  return gaps;
};

export default function VideoSessionTimelineLane({
  timeline,
  currentTime,
  viewport,
  syncOffset,
  notes,
  onTimeChange,
  onNoteClick,
  onNoteCreate,
  notesEnabled = false,
  laps = [],
  onLapClick,
}: VideoSessionTimelineLaneProps) {
  const { t } = useTranslation(["pages", "common"]);
  const visibleRange = viewport ?? timeline.range;
  const visibleDuration = Math.max(visibleRange.end - visibleRange.start, 0.1);
  const activeClip = getVideoSessionClipAtTime(timeline, currentTime);
  const gaps = useMemo(() => getTimelineGaps(timeline.clips), [timeline.clips]);
  const ticks = useMemo(
    () => {
      const timelineTicks = getVideoTimelineTicks(visibleDuration);
      const finalTick = timelineTicks[timelineTicks.length - 1];

      return timelineTicks
        .filter(
          (tick, index) =>
            index === 0 ||
            tick === finalTick ||
            !finalTick ||
            finalTick.positionPercent - tick.positionPercent >= 12,
        )
        .map((tick) => ({
          ...tick,
          sessionTime: visibleRange.start + tick.offsetSeconds,
        }));
    },
    [visibleDuration, visibleRange.start],
  );

  const getVisibleStyle = (startTime: number, endTime: number) => {
    const visibleStart = Math.max(startTime, visibleRange.start);
    const visibleEnd = Math.min(endTime, visibleRange.end);

    if (visibleEnd <= visibleStart) {
      return null;
    }

    return {
      left: `${((visibleStart - visibleRange.start) / visibleDuration) * 100}%`,
      width: `${((visibleEnd - visibleStart) / visibleDuration) * 100}%`,
    };
  };

  const getPointPosition = (time: number) =>
    `${((time - visibleRange.start) / visibleDuration) * 100}%`;

  const handleLaneClick = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(
      Math.max((event.clientX - bounds.left) / bounds.width, 0),
      1,
    );
    const sessionTime = visibleRange.start + ratio * visibleDuration;
    if (notesEnabled && onNoteCreate) {
      onNoteCreate(noteTimeOffsetFromSessionTime(sessionTime, syncOffset));
      return;
    }
    onTimeChange(sessionTime);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }

    event.preventDefault();
    const direction = event.key === "ArrowLeft" ? -1 : 1;
    const step = event.shiftKey ? 10 : 1;
    onTimeChange(
      Math.min(
        Math.max(currentTime + direction * step, visibleRange.start),
        visibleRange.end,
      ),
    );
  };

  const visibleNotes = notes.filter((note) => {
    const sessionTime = sessionTimeFromNoteTimeOffset(
      note.timeOffset,
      syncOffset,
    );
    return sessionTime >= visibleRange.start && sessionTime <= visibleRange.end;
  });
  const visibleLapMarkers = useMemo(
    () => getLapMarkers(laps, syncOffset, visibleRange),
    [laps, syncOffset, visibleRange],
  );
  const buildLapMarkerLabel = (marker: SessionLapMarker): string => {
    const label = t("pages:sessions.videoLane.ariaLapAt", {
      lap: marker.lap.lapNumber,
      time: formatVideoTimelineClock(
        timeline.anchorTimestamp,
        marker.sessionTime,
      ),
      lapTime: formatLapTime(marker.lap.lapTime * 1000),
    });
    return marker.lap.isValid === false
      ? `${label} ${t("pages:sessions.videoLane.lapInvalid")}`
      : label;
  };
  const cursorVisible =
    currentTime >= visibleRange.start && currentTime <= visibleRange.end;

  return (
    <section className="mx-0 mb-0 overflow-hidden rounded-none border-y border-[#222b3d] bg-[#090b12] lg:mx-3 lg:mb-3 lg:rounded-lg lg:border">
      <div className="flex h-9 items-center justify-between border-b border-[#20283a] bg-[#0d111b] px-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">
            {t("pages:sessions.videoLane.videoFiles")}
          </span>
          <span className="rounded-full border border-[#334155] px-2 py-0.5 text-[9px] text-[#64748b]">
            {t("pages:sessions.videoLane.clipsCount", { count: timeline.clips.length })}
          </span>
        </div>
        <span className="text-[9px] text-[#475569]">
          {t("pages:sessions.videoLane.sharesViewport")}
        </span>
      </div>

      <div className="relative h-6 text-[9px] tabular-nums text-[#475569]">
        {ticks.map((tick, index) => (
          <span
            key={`${tick.sessionTime}-${index}`}
            className={`absolute bottom-1 whitespace-nowrap ${
              index === 0
                ? "translate-x-0"
                : index === ticks.length - 1
                  ? "-translate-x-full"
                  : "-translate-x-1/2"
            }`}
            style={{ left: `${tick.positionPercent}%` }}
          >
            {formatVideoTimelineClock(
              timeline.anchorTimestamp,
              tick.sessionTime,
            )}
          </span>
        ))}
      </div>

      <div
        role="slider"
        tabIndex={0}
        aria-label={t("pages:sessions.videoLane.ariaTimeline")}
        aria-valuemin={visibleRange.start}
        aria-valuemax={visibleRange.end}
        aria-valuenow={Math.min(
          Math.max(currentTime, visibleRange.start),
          visibleRange.end,
        )}
        onClick={handleLaneClick}
        onKeyDown={handleKeyDown}
        className={`relative h-[72px] ${notesEnabled ? "cursor-cell" : "cursor-crosshair"} overflow-hidden border-y border-[#171f30] bg-[#07090f] outline-none focus-visible:ring-1 focus-visible:ring-[#60a5fa]`}
        style={{
          backgroundImage:
            "linear-gradient(90deg, #171f30 1px, transparent 1px)",
          backgroundSize: "12.5% 100%",
        }}
      >
        {timeline.clips.map((clip, index) => {
          const position = getVisibleStyle(clip.startTime, clip.endTime);
          if (!position) {
            return null;
          }

          const color = CLIP_COLORS[index % CLIP_COLORS.length];
          const isActive = activeClip?.video.id === clip.video.id;
          return (
            <div
              key={clip.video.id}
              className="absolute inset-y-2 flex items-center overflow-hidden rounded"
              style={{
                ...position,
                background: color.background,
                border: `1px solid ${color.border}`,
                boxShadow: isActive
                  ? "inset 0 0 0 1px #60a5fa, 0 0 12px rgba(59,130,246,0.28)"
                  : undefined,
              }}
              title={`${clip.video.filename} · ${formatVideoTimelineClock(
                timeline.anchorTimestamp,
                clip.startTime,
              )}–${formatVideoTimelineClock(
                timeline.anchorTimestamp,
                clip.endTime,
              )}`}
            >
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-15"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, transparent 0 48px, #bfdbfe 49px 50px)",
                }}
              />
              <span className="relative z-[1] truncate px-2 text-[10px] font-semibold text-[#dbeafe]">
                {clip.video.filename}
                <span className="ml-2 font-normal text-[#93c5fd]">
                  {formatVideoTimelineDuration(clip.duration)}
                </span>
              </span>
            </div>
          );
        })}

        {gaps.map((gap) => {
          const position = getVisibleStyle(gap.startTime, gap.endTime);
          if (!position) {
            return null;
          }
          return (
            <div
              key={`${gap.startTime}-${gap.endTime}`}
              className="absolute inset-y-2 grid place-items-center overflow-hidden whitespace-nowrap border-y border-[#6b4d13] text-[8px] font-medium text-[#fbbf24]"
              style={{
                ...position,
                backgroundImage:
                  "repeating-linear-gradient(135deg, #191510 0 4px, #30240e 4px 8px)",
              }}
              title={t("pages:sessions.videoLane.titleRecordingGap", {
                duration: formatVideoTimelineDuration(
                  gap.endTime - gap.startTime,
                ),
              })}
            >
              {t("pages:sessions.videoLane.gapLabel", {
                duration: formatVideoTimelineDuration(gap.endTime - gap.startTime),
              })}
            </div>
          );
        })}

        <TimelineNoteMarkers
          notes={visibleNotes}
          getSessionTime={(note) =>
            sessionTimeFromNoteTimeOffset(note.timeOffset, syncOffset)
          }
          getPointPosition={getPointPosition}
          buildAriaLabel={(note, sessionTime) =>
            t("pages:sessions.videoLane.ariaNoteAt", {
              time: formatVideoTimelineClock(
                timeline.anchorTimestamp,
                sessionTime,
              ),
              content: note.content,
            })
          }
          onNoteClick={onNoteClick}
        />

        <TimelineLapMarkers
          markers={visibleLapMarkers}
          getPointPosition={getPointPosition}
          buildAriaLabel={buildLapMarkerLabel}
          onLapClick={onLapClick}
        />

        {cursorVisible && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-30 w-px bg-[#3b82f6] shadow-[0_0_6px_rgba(59,130,246,0.75)] before:absolute before:left-[-3px] before:top-0 before:h-[7px] before:w-[7px] before:rounded-b before:bg-[#3b82f6]"
            style={{ left: getPointPosition(currentTime) }}
          />
        )}
      </div>

      <div className="flex h-7 items-center justify-between px-3 text-[9px] text-[#475569]">
        <span>
          <strong className="font-semibold text-[#93c5fd]">
            {formatVideoTimelineClock(timeline.anchorTimestamp, currentTime)}
          </strong>{" "}
          {t("pages:sessions.videoLane.sharedSessionCursor")}
        </span>
        <span>
          {activeClip
            ? `${activeClip.video.filename} ${t("pages:sessions.videoLane.localTime")}${formatVideoTimelineDuration(
                getVideoLocalTime(activeClip, currentTime),
              )}`
            : t("pages:sessions.videoLane.noVideoTelemetryAvailable")}
        </span>
      </div>
    </section>
  );
}
